<script lang="ts">
	import { showCookiePreferences } from '$lib/stores/cookies';
	import { _ } from 'svelte-i18n';

	interface FooterLink {
		label: string;
		href: string;
		external?: boolean;
		isButton?: boolean;
		onclick?: () => void;
	}

	interface FooterSection {
		title: string;
		links: FooterLink[];
	}

	function handleCookiePreferences() {
		// Show the cookie banner to allow changing preferences
		showCookiePreferences();
	}

	let { companyName = 'Heyweek', year = new Date().getFullYear() } = $props();

	const sections = $derived([
		{
			title: $_('footer.sections.product.title'),
			links: [
				{ label: $_('footer.sections.product.links.features'), href: '/features' },
				{ label: $_('footer.sections.product.links.pricing'), href: '/pricing' },
				{ label: $_('footer.sections.product.links.changelog'), href: '/changelog' },
				{ label: 'Web app', href: '/products/web' },
				{ label: 'Mobile app', href: '/products/mobile' },
				{ label: 'CLI', href: '/products/cli' },
				{ label: 'Browser extensions', href: '/products/extensions' }
			]
		},
		{
			title: 'Features',
			links: [
				{ label: 'Time tracking', href: '/features/time-tracking' },
				{ label: 'Todos', href: '/features/todos' },
				{ label: 'Tasks', href: '/features/tasks' },
				{ label: 'Pulse', href: '/features/pulse' },
				{ label: 'Projects', href: '/features/project-management' },
				{ label: 'Invoicing', href: '/features/invoicing' },
				{ label: 'Clients', href: '/features/clients' },
				{ label: 'People', href: '/features/people' },
				{ label: 'Absence', href: '/features/absence' },
				{ label: 'Reports', href: '/features/reports' }
			]
		},
		{
			title: $_('footer.sections.company.title'),
			links: [
				{ label: $_('footer.sections.company.links.about'), href: '/about' },
				{ label: $_('footer.sections.company.links.customers'), href: '/customers' },
				{ label: 'Success stories', href: '/stories' },
				{ label: $_('footer.sections.company.links.team'), href: '/team' },
				{ label: $_('footer.sections.company.links.press'), href: '/press-kit' },
				{ label: 'Press', href: '/press' }
			]
		},
		{
			title: $_('footer.sections.resources.title'),
			links: [
				{ label: $_('footer.sections.resources.links.documentation'), href: '/docs' },
				{ label: $_('footer.sections.resources.links.help'), href: '/docs/contact-support' },
				{ label: 'FAQ', href: '/faq' },
				{ label: $_('footer.sections.resources.links.blog'), href: '/blog' },
				{ label: 'Weekly', href: '/weekly' },
				{ label: 'Getting started', href: '/getting-started' },
				{ label: 'Troubleshooting', href: '/docs/troubleshooting' }
			]
		},
		{
			title: $_('footer.sections.legal.title'),
			links: [
				{ label: $_('footer.sections.legal.links.privacy'), href: '/privacy-policy' },
				{ label: $_('footer.sections.legal.links.terms'), href: '/terms-of-use' },
				{
					label: $_('footer.sections.legal.links.cookies'),
					href: '#',
					isButton: true,
					onclick: handleCookiePreferences
				}
			]
		},
		{
			title: $_('footer.sections.connect.title'),
			links: [
				{
					label: $_('footer.sections.connect.links.twitter'),
					href: 'https://twitter.com/heyweekapp',
					external: true
				},
				{
					label: $_('footer.sections.connect.links.linkedin'),
					href: 'https://www.linkedin.com/company/heyweek/',
					external: true
				}
			]
		}
	]);
</script>

<footer class="footer" id="about">
	<div class="container">
		<!-- Top section with logo and newsletter -->
		<div class="footer__top">
			<div class="footer__brand">
				<a href="/" class="brand__logo" aria-label={$_('footer.logoAriaLabel')}>
					<img
						src="/favicon.png"
						alt={$_('footer.logoAlt')}
						class="logo-image"
						width="32"
						height="32"
					/>
				</a>
				<p class="brand__tagline">Run your entire business. One tab.</p>
				<p class="brand__copy">© {year} Heyweek. All rights reserved.</p>
			</div>

			<!-- Links sections -->
			<div class="footer__links">
				{#each sections as section}
					<div class="links__section">
						<h3 class="links__title">{section.title}</h3>
						<ul class="links__list">
							{#each section.links as link}
								<li>
									{#if link.isButton}
										<button class="links__item links__item--button" onclick={link.onclick}>
											{link.label}
										</button>
									{:else}
										<a
											href={link.href}
											class="links__item"
											target={link.external ? '_blank' : undefined}
											rel={link.external ? 'noopener noreferrer' : undefined}
										>
											{link.label}
										</a>
									{/if}
								</li>
							{/each}
						</ul>
					</div>
				{/each}
			</div>
		</div>
	</div>
</footer>

<style>
	.footer {
		background: var(--bg-secondary);
		border-top: 1px solid var(--border-light);
		padding-block: var(--space-8) var(--space-6);
		margin-top: var(--space-24);
	}

	.container {
		max-width: var(--container-max-width);
		margin-inline: auto;
		padding-inline: var(--container-padding);
	}

	/* Top section */
	.footer__top {
		display: grid;
		grid-template-columns: 1fr 2fr;
		gap: var(--space-8);
		padding-bottom: var(--space-6);
	}

	.footer__brand {
		display: flex;
		flex-direction: column;
	}

	.brand__logo {
		display: inline-block;
		text-decoration: none;
		margin-bottom: var(--space-2);
		opacity: 0.8;
		transition: opacity 0.2s ease;
	}

	.brand__logo:hover {
		opacity: 1;
	}

	.logo-image {
		height: 24px;
		width: 24px;
	}

	.brand__tagline {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		margin-top: var(--space-3);
		line-height: 1.4;
	}

	.brand__copy {
		font-size: var(--font-size-xs);
		color: var(--text-tertiary, var(--text-secondary));
		opacity: 0.6;
		margin-top: var(--space-2);
	}

	/* Links sections */
	.footer__links {
		display: grid;
		grid-template-columns: repeat(5, 1fr);
		gap: var(--space-4);
	}

	.links__title {
		font-size: 12px;
		font-weight: 600;
		color: var(--text-tertiary);
		text-transform: uppercase;
		letter-spacing: 0.05em;
		margin-bottom: var(--space-2);
		opacity: 0.7;
	}

	.links__list {
		list-style: none;
		margin: 0;
		padding: 0;
		display: flex;
		flex-direction: column;
		gap: 2px;
	}

	.links__item {
		font-size: 13px;
		color: var(--text-secondary);
		text-decoration: none;
		transition: var(--transition-colors);
		line-height: 1.6;
		padding: 2px 0;
	}

	.links__item:hover {
		color: var(--text-primary);
	}

	.links__item--button {
		background: transparent;
		border: none;
		padding: 2px 0;
		cursor: pointer;
		font: inherit;
		text-align: left;
		width: auto;
		font-size: 13px;
		color: var(--text-secondary);
		line-height: 1.6;
		transition: var(--transition-colors);
	}

	.links__item--button:hover {
		color: var(--text-primary);
	}

	/* Responsive */
	@media (max-width: 1200px) {
		.footer__links {
			grid-template-columns: repeat(3, 1fr);
		}
	}

	@media (max-width: 1024px) {
		.footer__links {
			grid-template-columns: repeat(2, 1fr);
		}
	}

	@media (max-width: 768px) {
		.footer__top {
			grid-template-columns: 1fr;
		}

		.footer__links {
			grid-template-columns: repeat(2, 1fr);
		}
	}

	@media (max-width: 480px) {
		.footer__links {
			grid-template-columns: 1fr;
			text-align: center;
		}

		.footer__brand {
			align-items: center;
			text-align: center;
		}
	}
</style>
